import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { TreslogEmptyState } from '@/components/treslog/TreslogEmptyState';
import { TreslogEntryCard } from '@/components/treslog/TreslogEntryCard';
import { TreslogEntryEditor } from '@/components/treslog/TreslogEntryEditor';
import { TreslogEntryRevealDialog } from '@/components/treslog/TreslogEntryRevealDialog';
import { TreslogAccessGate } from '@/components/treslog/TreslogAccessGate';
import { TreslogOnboarding } from '@/components/treslog/TreslogOnboarding';
import { MoodMeter } from '@/components/treslog/MoodMeter';
import { MoodHistory } from '@/components/treslog/MoodHistory';
import { MoodTrendChart } from '@/components/treslog/MoodTrendChart';
import { useTreslog } from '@/hooks/useTreslog';
import { useMoodEntries } from '@/hooks/useMoodEntries';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useLanguage } from '@/i18n/LanguageContext';
import { toast } from 'sonner';
import type { TreslogEntry } from '@/types/treslog';

export default function Treslog() {
  const { language } = useLanguage();
  const { trackTreslogViewed } = useAnalytics();
  
  const {
    entries,
    isLoading,
    hasLegacyAccess,
    createEntry,
    updateEntry,
    deleteEntry,
    uploadPhoto,
  } = useTreslog();

  const {
    entries: moodEntries,
    todayEntry: todayMood,
    isLoading: moodLoading,
    createEntry: createMoodEntry,
    updateEntry: updateMoodEntry,
    deleteEntry: deleteMoodEntry,
  } = useMoodEntries();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TreslogEntry | null>(null);
  const [viewingEntry, setViewingEntry] = useState<TreslogEntry | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const content = {
    en: {
      title: 'TRESLOG',
      subtitle: 'Your Private Lifetime Reflection Space',
      newEntry: 'New Entry',
      loading: 'Loading your entries...',
      badge: 'Private • Lifetime • Yours Forever',
    },
    es: {
      title: 'TRESLOG',
      subtitle: 'Tu Espacio Privado de Reflexión de por Vida',
      newEntry: 'Nueva Entrada',
      loading: 'Cargando tus entradas...',
      badge: 'Privado • De por Vida • Tuyo para Siempre',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  // Track page view
  useState(() => {
    trackTreslogViewed('dashboard');
  });

  const handleCreateNew = () => {
    setEditingEntry(null);
    setEditorOpen(true);
  };

  const handleView = (entry: TreslogEntry) => {
    setViewingEntry(entry);
    setViewDialogOpen(true);
  };

  const handleEdit = (entry: TreslogEntry) => {
    setEditingEntry(entry);
    setEditorOpen(true);
  };

  const handleSave = async (data: any) => {
    if (editingEntry) {
      await updateEntry(editingEntry.id, data);
    } else {
      await createEntry(data);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEntry(id);
      toast.success('Entry deleted');
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleTogglePrivacy = async (id: string, isPublic: boolean) => {
    try {
      await updateEntry(id, { is_public: isPublic });
      toast.success(isPublic ? 'Entry is now public' : 'Entry is now private');
    } catch (error) {
      toast.error('Failed to update privacy');
    }
  };

  const handleMoodSave = async (moodValue: number, moodLabel: string, isPublic: boolean, notes?: string) => {
    if (todayMood) {
      await updateMoodEntry(todayMood.id, { mood_value: moodValue, mood_label: moodLabel, is_public: isPublic, notes: notes || null });
    } else {
      await createMoodEntry(moodValue, moodLabel, isPublic, notes);
    }
  };

  const handleMoodDelete = async (id: string) => {
    try {
      await deleteMoodEntry(id);
      toast.success('Mood entry deleted');
    } catch (error) {
      toast.error('Failed to delete mood entry');
    }
  };

  // Show access gate if user doesn't have Legacy plan
  if (!isLoading && !hasLegacyAccess) {
    return (
      <div className="min-h-screen bg-hero-gradient">
        <AuthenticatedHeader />
        <main className="container py-8">
          <TreslogAccessGate />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <AuthenticatedHeader />
      
      {/* Onboarding slideshow for first-time users */}
      <TreslogOnboarding onComplete={() => {}} />
      <main className="container py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t.title}
                </h1>
                <p className="text-muted-foreground text-sm">{t.subtitle}</p>
                <p className="text-xs text-primary mt-1">{t.badge}</p>
              </div>
            </div>

            {entries.length > 0 && (
              <Button onClick={handleCreateNew} className="gap-2">
                <Plus className="h-4 w-4" />
                {t.newEntry}
              </Button>
            )}
          </div>

          {/* Mood Meter Section */}
          <div className="grid gap-6 lg:grid-cols-2 mb-6">
            <MoodMeter
              onSave={handleMoodSave}
              currentMood={
                todayMood
                  ? { 
                      value: todayMood.mood_value, 
                      label: todayMood.mood_label, 
                      isPublic: todayMood.is_public, 
                      notes: todayMood.notes || undefined,
                      createdAt: todayMood.created_at,
                    }
                  : null
              }
              isLoading={moodLoading}
            />
            <div className="rounded-2xl border border-border/50 bg-card p-4">
              <MoodHistory
                entries={moodEntries}
                onDelete={handleMoodDelete}
                isLoading={moodLoading}
              />
            </div>
          </div>

          {/* Mood Trend Chart */}
          <div className="mb-8">
            <MoodTrendChart entries={moodEntries} isLoading={moodLoading} />
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-pulse text-muted-foreground">{t.loading}</div>
            </div>
          ) : entries.length === 0 ? (
            <TreslogEmptyState onCreateFirst={handleCreateNew} />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {entries.map((entry) => (
                  <TreslogEntryCard
                    key={entry.id}
                    entry={entry}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onTogglePrivacy={handleTogglePrivacy}
                    onView={handleView}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </main>

      {/* Entry Editor Modal */}
      <TreslogEntryEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        entry={editingEntry}
        onSave={handleSave}
        onUploadPhoto={uploadPhoto}
      />

      {/* Entry Reveal Dialog — pen-writing animation */}
      <TreslogEntryRevealDialog
        entry={viewingEntry}
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
      />
    </div>
  );
}
